import { useState, useMemo } from 'react';
import {
  GripVertical,
  Plus,
  Trash2,
  Check,
  X,
  Loader2,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn, getContrastTextColor } from '@/lib/utils';
import type { TaskStatus } from '@/types';

const STATUS_COLORS = [
  '#6b7280', // gray
  '#ef4444', // red
  '#f97316', // orange
  '#f59e0b', // amber
  '#eab308', // yellow
  '#84cc16', // lime
  '#22c55e', // green
  '#10b981', // emerald
  '#14b8a6', // teal
  '#06b6d4', // cyan
  '#0ea5e9', // sky
  '#3b82f6', // blue
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#a855f7', // purple
  '#d946ef', // fuchsia
  '#ec4899', // pink
  '#f43f5e', // rose
];

interface StatusEditorProps {
  statuses: TaskStatus[];
  isLoading: boolean;
  onCreate: (data: { name: string; color: string; isDoneState: boolean }) => Promise<void>;
  onUpdate: (statusId: string, data: { name?: string; color?: string; isDoneState?: boolean }) => Promise<void>;
  onDelete: (statusId: string) => Promise<void>;
  onReorder: (statusIds: string[]) => Promise<void>;
  isCreating?: boolean;
  isUpdating?: boolean;
}

interface EditingStatus {
  id: string;
  name: string;
  color: string;
  isDoneState: boolean;
}

export function StatusEditor({
  statuses,
  isLoading,
  onCreate,
  onUpdate,
  onDelete,
  onReorder,
  isCreating,
  isUpdating,
}: StatusEditorProps) {
  const [editing, setEditing] = useState<EditingStatus | null>(null);
  const [newStatus, setNewStatus] = useState<{
    name: string;
    color: string;
    isDoneState: boolean;
  } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Sort by position and separate active and done statuses
  const orderedStatuses = useMemo(
    () => [...statuses].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [statuses]
  );
  const activeStatuses = orderedStatuses.filter((s) => !s.isDoneState);
  const doneStatuses = orderedStatuses.filter((s) => s.isDoneState);
  const statusIds = useMemo(() => orderedStatuses.map((s) => s.id), [orderedStatuses]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedStatuses.findIndex((s) => s.id === active.id);
    const newIndex = orderedStatuses.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(orderedStatuses, oldIndex, newIndex);
    onReorder(reordered.map((s) => s.id));
  };

  const handleStartEdit = (status: TaskStatus) => {
    setEditing({
      id: status.id,
      name: status.name,
      color: status.color,
      isDoneState: status.isDoneState,
    });
  };

  const handleCancelEdit = () => {
    setEditing(null);
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    const original = statuses.find((s) => s.id === editing.id);
    if (!original) return;

    const changes: { name?: string; color?: string; isDoneState?: boolean } = {};
    if (editing.name !== original.name) changes.name = editing.name;
    if (editing.color !== original.color) changes.color = editing.color;
    if (editing.isDoneState !== original.isDoneState) changes.isDoneState = editing.isDoneState;

    if (Object.keys(changes).length > 0) {
      await onUpdate(editing.id, changes);
    }
    setEditing(null);
  };

  const handleStartAdd = () => {
    setNewStatus({
      name: '',
      color: '#6b7280',
      isDoneState: false,
    });
  };

  const handleCancelAdd = () => {
    setNewStatus(null);
  };

  const handleSaveAdd = async () => {
    if (!newStatus || !newStatus.name.trim()) return;
    await onCreate({
      name: newStatus.name.trim(),
      color: newStatus.color,
      isDoneState: newStatus.isDoneState,
    });
    setNewStatus(null);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={statusIds} strategy={verticalListSortingStrategy}>
          {/* Active statuses */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Active Statuses</h4>
            <div className="space-y-1">
              {activeStatuses.map((status) => (
                <SortableStatusRow
                  key={status.id}
                  status={status}
                  editing={editing?.id === status.id ? editing : null}
                  isDeleting={deletingId === status.id}
                  onStartEdit={() => handleStartEdit(status)}
                  onCancelEdit={handleCancelEdit}
                  onSaveEdit={handleSaveEdit}
                  onDelete={() => handleDelete(status.id)}
                  onEditChange={(updates) => setEditing((e) => (e ? { ...e, ...updates } : null))}
                  isUpdating={isUpdating}
                />
              ))}
            </div>
          </div>

          {/* Done statuses */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Done Statuses</h4>
            <div className="space-y-1">
              {doneStatuses.map((status) => (
                <SortableStatusRow
                  key={status.id}
                  status={status}
                  editing={editing?.id === status.id ? editing : null}
                  isDeleting={deletingId === status.id}
                  onStartEdit={() => handleStartEdit(status)}
                  onCancelEdit={handleCancelEdit}
                  onSaveEdit={handleSaveEdit}
                  onDelete={() => handleDelete(status.id)}
                  onEditChange={(updates) => setEditing((e) => (e ? { ...e, ...updates } : null))}
                  isUpdating={isUpdating}
                />
              ))}
              {doneStatuses.length === 0 && (
                <p className="text-xs text-muted-foreground py-2">No done statuses yet</p>
              )}
            </div>
          </div>
        </SortableContext>
      </DndContext>

      {/* Add new status */}
      {newStatus ? (
        <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/30">
          <ColorPicker
            color={newStatus.color}
            onChange={(color) => setNewStatus({ ...newStatus, color })}
          />
          <Input
            value={newStatus.name}
            onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })}
            placeholder="Status name"
            className="flex-1 h-8"
            autoFocus
          />
          <div className="flex items-center gap-1">
            <Label htmlFor="new-done" className="text-xs">Done</Label>
            <Switch
              id="new-done"
              checked={newStatus.isDoneState}
              onCheckedChange={(checked: boolean) => setNewStatus({ ...newStatus, isDoneState: checked })}
            />
          </div>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={handleSaveAdd}
            disabled={!newStatus.name.trim() || isCreating}
          >
            {isCreating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={handleCancelAdd}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-2"
          onClick={handleStartAdd}
        >
          <Plus className="h-4 w-4" />
          Add Status
        </Button>
      )}
    </div>
  );
}

interface StatusRowProps {
  status: TaskStatus;
  editing: EditingStatus | null;
  isDeleting: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDelete: () => void;
  onEditChange: (updates: Partial<EditingStatus>) => void;
  isUpdating?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}

function StatusRow({
  status,
  editing,
  isDeleting,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onEditChange,
  isUpdating,
  dragHandleProps,
}: StatusRowProps) {
  const isEditing = !!editing;

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/30">
        <ColorPicker
          color={editing.color}
          onChange={(color) => onEditChange({ color })}
        />
        <Input
          value={editing.name}
          onChange={(e) => onEditChange({ name: e.target.value })}
          className="flex-1 h-8"
          autoFocus
        />
        <div className="flex items-center gap-1">
          <Label htmlFor={`done-${status.id}`} className="text-xs">Done</Label>
          <Switch
            id={`done-${status.id}`}
            checked={editing.isDoneState}
            onCheckedChange={(checked: boolean) => onEditChange({ isDoneState: checked })}
          />
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          onClick={onSaveEdit}
          disabled={isUpdating}
        >
          {isUpdating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          onClick={onCancelEdit}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="group flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors">
      <div
        className="touch-none cursor-grab active:cursor-grabbing text-muted-foreground opacity-0 group-hover:opacity-100 flex items-center"
        {...(dragHandleProps ?? {})}
      >
        <GripVertical className="h-4 w-4" />
      </div>
      {(() => {
        const sBg = status.color || '#6b7280';
        const sText = getContrastTextColor(sBg);
        return (
          <Badge
            className="cursor-pointer font-medium"
            style={{ backgroundColor: sBg, color: sText, borderColor: 'transparent' }}
            onClick={onStartEdit}
          >
            {status.name}
          </Badge>
        );
      })()}
      <span className="flex-1" />
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
        onClick={onDelete}
        disabled={isDeleting}
      >
        {isDeleting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Trash2 className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
}

function SortableStatusRow(props: StatusRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.status.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <StatusRow
        {...props}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
}

function ColorPicker({ color, onChange }: ColorPickerProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="h-6 w-6 rounded-md border cursor-pointer"
          style={{ backgroundColor: color }}
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <div className="grid grid-cols-6 gap-1">
          {STATUS_COLORS.map((c) => (
            <button
              key={c}
              className={cn(
                'h-6 w-6 rounded-md border cursor-pointer',
                color === c && 'ring-2 ring-primary ring-offset-2'
              )}
              style={{ backgroundColor: c }}
              onClick={() => onChange(c)}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
